import { v4 as uuidv4 } from 'uuid';
import { getFY, getFyNumber } from '../../utils/calculation';
import { Investment } from '../../models/investment';
import produce from 'immer';
import { formatToOriginal } from '../../store/utils/utils';
import { getUnitCategory } from '../../components/SiteTab/BUInputs/utlis';
import { SiteLocationBuType } from '../../store/projects/types';
import { shouldAutoPromoteToDraft } from '../../utils/fieldEditingRules';
import { PartOfCrp, ProjectSolutionType } from '../../models/project';

export const establishmentTypes = [
  { label: 'Establish new', value: 'NEW' },
  { label: 'Establish new Lada store', value: 'LADA' },
  { label: 'Improve Existing (SISAN)', value: 'SISAN' },
  { label: 'Change Format', value: 'FORMAT_TRANSFORMATION' },
  { label: 'Relocate', value: 'RELOCATION' },
  { label: 'Close', value: 'CLOSE' },
];
interface InvestmentDTO {
  fiscalYear: string; // "FY26"
  landCost: number | string | null;
  retailCost: number | string | null;
  propertyInvestment: number | string | null;
  retailInvestment: number | string | null;
}

export interface ProjectDTO {
  recordId: string;
  id: string;
  projectName: string;
  countryCode: string;
  businessUnitId: string;
  unitType: string;
  newUnitType: string;
  unitCategory: string;
  confidential: string;
  transactionType: string;
  targetProjectEndDate: string;
  targetProjectStartDate: string;
  countryProjectResponsible: string;
  siteLocation: { [key: string]: unknown };
  establishmentType: string;
  investments: InvestmentDTO[];
  partOfCrp: PartOfCrp[];
  solutions: ProjectSolutionType[];
}
const emptyInvestment = (fy: string): InvestmentDTO => ({
  fiscalYear: fy,
  landCost: '0',
  retailCost: '0',
  propertyInvestment: '0',
  retailInvestment: '0',
});
export function createProject({
  recordId = uuidv4(),
  projectName = null,
  countryCode = null,
  businessUnitId = null,
  unitType = null,
  newUnitType = null,
  unitCategory = null,
  confidential = null,
  transactionType = null,
  targetProjectStartDate = new Date().toISOString().slice(0, 10),
  targetProjectEndDate = new Date().toISOString().slice(0, 10),
  countryProjectResponsible = null,
  siteLocation = null,
  establishmentType = null,
  solutions = [],
  partOfCrp = [],
  investments = [
    emptyInvestment(getFY()),
    emptyInvestment(getFY(1)),
    emptyInvestment(getFY(2)),
  ],
}: Partial<ProjectDTO> = {}): ProjectDTO {
  return {
    recordId,
    id: recordId,
    projectName,
    countryCode,
    businessUnitId,
    newUnitType,
    unitType,
    unitCategory: establishmentType === 'LADA' ? 'CMP' : unitCategory,
    confidential,
    transactionType,
    targetProjectStartDate,
    targetProjectEndDate,
    siteLocation,
    countryProjectResponsible,
    establishmentType,
    investments,
    solutions,
    partOfCrp,
  };
}

export const BULK_CREATE_FIELDS = [
  'projectName',
  'countryCode',
  'unitType',
  'unitCategory',
  'confidential',
  'transactionType',
  'targetProjectStartDate',
  'targetProjectEndDate',
  'countryProjectResponsible',
];

export const FORMAT_TRANSFORMATION_FIELDS = [
  ...BULK_CREATE_FIELDS,
  'businessUnitId',
  'newUnitType',
];

export const returnRequiredFields = (establishmentType: string) => {
  switch (establishmentType) {
    case 'NEW':
    case 'LADA':
      return BULK_CREATE_FIELDS;
    case 'FORMAT_TRANSFORMATION':
      return FORMAT_TRANSFORMATION_FIELDS;
    default:
      return [...BULK_CREATE_FIELDS, 'businessUnitId'];
  }
};

const normalizeInvestments: (
  investments: InvestmentDTO[],
  startFY: number,
  endFY: number,
) => InvestmentDTO[] = (investments, startFY, endFY) => {
  const map = new Map(investments.map((inv) => [inv.fiscalYear, inv]));

  const result: InvestmentDTO[] = [];

  for (let fy = startFY; fy <= endFY; fy++) {
    const fyKey = `FY${fy}`;
    if (map.has(fyKey)) {
      result.push(map.get(fyKey));
    } else {
      result.push(emptyInvestment(fyKey));
    }
  }

  return result;
};

export const flattenProjects = (projects: ProjectDTO[]) => {
  let minFY: number | null = null;
  let maxFY: number | null = null;

  const flattened = projects.map((project) => {
    const flat: any = {
      recordId: project.recordId,
      id: project.id,
      projectName: project.projectName,
    };

    for (const inv of project.investments) {
      const fyNum = getFyNumber(inv.fiscalYear);

      if (!isNaN(fyNum)) {
        if (minFY === null || fyNum < minFY) minFY = fyNum;
        if (maxFY === null || fyNum > maxFY) maxFY = fyNum;
      }

      flat[`${inv.fiscalYear}-landCost`] = inv.landCost ?? null;
      flat[`${inv.fiscalYear}-retailCost`] = inv.retailCost ?? null;
      flat[`${inv.fiscalYear}-propertyInvestment`] =
        inv.propertyInvestment ?? null;
      flat[`${inv.fiscalYear}-retailInvestment`] = inv.retailInvestment ?? null;
    }

    return flat;
  });

  return {
    projects: flattened,
    minFY,
    maxFY,
  };
};

export const onRowChangeHandler = (
  id: string,
  field: string,
  value: string | SiteLocationBuType,
  projects,
  setProjects,
  bus,
) => {
  const newProjects = produce(projects, (draft) => {
    const project = draft.find((p) => p.recordId === id);
    if (project) {
      project[field] = value;
      if (
        field === 'partOfCrp' &&
        shouldAutoPromoteToDraft(
          project.projectStatus,
          value as unknown as PartOfCrp[],
          project.projectSource,
        )
      ) {
        project.projectStatus = 'DRAFT';
      }
      if (field === 'siteLocation') {
        const siteLocationBuType = value as SiteLocationBuType;
        const businessUnit = bus.find(
          (b) => b.businessUnitId === siteLocationBuType.businessUnitId[0],
        );
        project.businessUnitId = siteLocationBuType.businessUnitId;
        project.countryCode = businessUnit?.countryId;
        project.unitCategory = getUnitCategory(businessUnit.businessUnitId);
        project.unitType = null;
      }
      if (
        field === 'unitType' &&
        project[field].toLowerCase().indexOf('lada') > -1
      ) {
        project.programmeName = 'lada';
      }
      if (
        field === 'targetProjectStartDate' &&
        new Date(value as string) > new Date(project.targetProjectEndDate)
      ) {
        project.targetProjectEndDate = value;
      }
      if (
        field === 'targetProjectEndDate' &&
        new Date(value as string) > new Date(project.targetProjectStartDate)
      ) {
        project.targetProjectEndDate = value;
      }

      // EXTEND FISCAL YEARS
      if (
        field === 'targetProjectStartDate' ||
        field === 'targetProjectEndDate'
      ) {
        project.investments = normalizeInvestments(
          project.investments,
          getFyNumber(getFY(0, project.targetProjectStartDate)),
          getFyNumber(getFY(2, project.targetProjectEndDate)),
        );
      }
    }
  });
  setProjects(newProjects);
};

export const unflattenProject = (project) => {
  const baseFields: any = {};
  const investmentsMap: Record<string, Investment> = {};

  for (const key in project) {
    if (key.includes('FY')) {
      const [fyPart, field] = key.split('-');
      const fiscalYear = fyPart;

      if (!investmentsMap[fiscalYear]) {
        investmentsMap[fiscalYear] = {
          fiscalYear,
          landCost: null,
          retailCost: null,
          propertyInvestment: null,
          retailInvestment: null,
        };
      }

      investmentsMap[fiscalYear][field as keyof Investment] = project[key];
    } else {
      baseFields[key] = project[key];
    }
  }

  return {
    ...baseFields,
    investments: Object.values(investmentsMap),
  };
};

export const returnUnflattenedMap = (projects) => {
  const map = new Map();
  projects.forEach((project) =>
    map.set(project.recordId, unflattenProject(project)),
  );
  return map;
};
export const returnUnflattenedMapWithFinancials = (finProjects, projects) => {
  const projectsWithFinancials = returnUnflattenedMap(finProjects);
  return projects.map((p) => ({
    ...p,
    ...projectsWithFinancials.get(p.recordId),
  }));
};

export const stripOfNewId = (solution) => {
  const solutionCopy = { ...solution };
  if (solutionCopy.id.includes('unsaved-')) {
    delete solutionCopy.id;
  }
  return solutionCopy;
};

export const mapProjectsForRequest = (projects) =>
  projects.map((p) => {
    const base = { ...p };
    if (['LADA', 'NEW'].includes(p.establishmentType)) {
      base.establishmentType = 'NEW';
    }

    base.businessUnitId = p.businessUnitId && p.businessUnitId[0];
    const investments = p.investments.map((inv) => ({
      ...inv,
      landCost: formatToOriginal(inv.landCost),
      retailCost: formatToOriginal(inv.retailCost),
      propertyInvestment: formatToOriginal(inv.propertyInvestment),
      retailInvestment: formatToOriginal(inv.retailInvestment),
    }));
    const solutions = p.solutions.map(stripOfNewId);
    return { ...base, investments, solutions };
  });
